type PathSegment = string | number

function isNumericSegment(segment: string): boolean {
	if (segment.length === 0) return false
	if (segment.startsWith('0') && segment !== '0') return false
	return /^\d+$/.test(segment)
}

function normalizePath(path: string | readonly PathSegment[]): PathSegment[] {
	if (Array.isArray(path)) {
		return [...path] as PathSegment[]
	}
	const raw = path as string
	const segments: PathSegment[] = []
	for (const part of raw.split('.')) {
		if (!part) continue
		const matches = part.split(/\[|\]/).filter(Boolean)
		for (const match of matches) {
			if (isNumericSegment(match)) {
				segments.push(Number(match))
			} else {
				segments.push(match)
			}
		}
	}
	return segments
}

function cloneContainer(container: unknown, nextKey: PathSegment) {
	if (Array.isArray(container)) return [...container]
	if (container && typeof container === 'object')
		return { ...(container as Record<string, unknown>) }
	return typeof nextKey === 'number' ? [] : {}
}

export function getValueAtPath<Container = unknown, Value = unknown>(
	source: Container,
	path: string | readonly PathSegment[],
): Value | undefined {
	const segments = normalizePath(path)
	let current: unknown = source
	for (const segment of segments) {
		if (current === null || typeof current !== 'object') return undefined
		if (Array.isArray(current) && typeof segment === 'number') {
			current = current[segment]
		} else {
			current = (current as Record<string, unknown>)[String(segment)]
		}
	}
	return current as Value | undefined
}

export function setValueAtPath<Value>(
	source: Value,
	path: string | readonly PathSegment[],
	value: unknown,
): Value {
	const segments = normalizePath(path)
	if (segments.length === 0) {
		return value as Value
	}
	const [headRaw, ...tail] = segments
	const head = headRaw as PathSegment
	const container = cloneContainer(source, head)

	if (tail.length === 0) {
		if (Array.isArray(container) && typeof head === 'number') {
			const next = container.slice()
			next[head] = value
			return next as Value
		}
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const result: Value = {
			...(container as Record<string, unknown>),
			[String(head)]: value,
		} as Value
		return result
	}

	let nextValue: unknown
	if (Array.isArray(container) && typeof head === 'number') {
		nextValue = setValueAtPath(container[head], tail, value)
		const next = container.slice()
		next[head] = nextValue
		return next as Value
	}

	const currentChild =
		container && typeof container === 'object'
			? (container as Record<string, unknown>)[String(head)]
			: undefined
	nextValue = setValueAtPath(currentChild, tail, value)
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const result: Value = {
		...(container as Record<string, unknown>),
		[String(head)]: nextValue,
	} as Value
	return result
}

export function updateValueAtPath<Value>(
	source: Value,
	path: string | readonly PathSegment[],
	updater: (current: unknown) => unknown,
): Value {
	const segments = normalizePath(path)
	if (segments.length === 0) {
		return updater(source) as Value
	}
	const [headRaw, ...tail] = segments
	const head = headRaw as PathSegment
	const container = cloneContainer(source, head)

	if (tail.length === 0) {
		const nextValue = updater(
			Array.isArray(container) && typeof head === 'number'
				? container[head]
				: (container as Record<string, unknown>)[String(head)],
		)
		if (Array.isArray(container) && typeof head === 'number') {
			const next = container.slice()
			next[head] = nextValue
			return next as Value
		}
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const result: Value = {
			...(container as Record<string, unknown>),
			[String(head)]: nextValue,
		} as Value
		return result
	}

	const child =
		Array.isArray(container) && typeof head === 'number'
			? container[head]
			: (container as Record<string, unknown>)[String(head)]

	const nextChild = updateValueAtPath(child, tail, updater)

	if (Array.isArray(container) && typeof head === 'number') {
		const next = container.slice()
		next[head] = nextChild
		return next as Value
	}

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const result: Value = {
		...(container as Record<string, unknown>),
		[String(head)]: nextChild,
	} as Value
	return result
}
